import React, { useContext, useEffect, useRef, useState } from 'react'
import { isMobileOrTablet } from '../../pages/Home/helpers'
import { AccountContext } from '../../providers/accountProvider'
import CrossIcon from '../Images/CrossIcon'
import EnterFullScreenIcon from '../Images/EnterFullScreenIcon'
import ExitFullScreenIcon from '../Images/ExitFullScreenIcon'
import PauseIcon from '../Images/PauseIcon'
import PlayIcon from '../Images/PlayIcon'
import VolumeMuteIcon from '../Images/VolumeMuteIcon'
import VolumeUpIcon from '../Images/VolumeUpIcon'
import {
  VideoPlayerContainer,
  VideoContainer,
  Video,
  VideoControls,
  VideoPlaybackControls,
  VideoSeekBar,
  PlayAndPauseBtn,
  FullScreenBtn,
  MuteAndUnmuteBtn,
  UnmuteBtn,
  CloseVideoPlayerBtn,
  VideoTimings,
  Time,
} from './styles'

const VideoPlayer = ({
  videoSrc,
  videoType,
  videoMuted,
  videoAutoPlay,
  videoLoop,
  hideVideoPlayer,
  isSeries,
}) => {
  const { isHindiUgcUser } = useContext(AccountContext)
  const checkIsFullScreen = () =>
    !!(
      document.webkitIsFullScreen ||
      document.mozFullScreen ||
      document.msFullscreenElement ||
      document.fullscreenElement
    )

  const [isVideoFullScreen, setIsVideoFullScreen] = useState(
    checkIsFullScreen()
  )
  const [isVideoPaused, setIsVideoPaused] = useState(videoAutoPlay)
  const [isVideoMuted, setIsVideoMuted] = useState(videoMuted)
  const [totalDurationHours, setTotalDurationHours] = useState('00')
  const [totalDurationMins, setTotalDurationMins] = useState('00')
  const [totalDurationSecs, setTotalDurationSecs] = useState('00')
  const [currentTimeHours, setCurrentTimeHours] = useState('00')
  const [currentTimeMins, setCurrentTimeMins] = useState('00')
  const [currentTimeSecs, setCurrentTimeSecs] = useState('00')
  const [seekbarPercentage, setSeekbarPercentage] = useState(0)
  const [isControlsVisible, setIsControlsVisible] = useState(videoAutoPlay)
  const [isUnmuteBtnVisible, setIsUnmuteBtnVisible] = useState(videoAutoPlay)
  const videoElementRef = useRef(null)
  const videoContainerElementRef = useRef(null)

  useEffect(() => {
    document.addEventListener('fullscreenchange', () => {
      checkIsFullScreen()
        ? setIsVideoFullScreen(true)
        : setIsVideoFullScreen(false)
    })
  }, [])

  const calculatePadding = (num) =>
    num > -10 && num < 10 ? '0' + num : num.toString()

  const calculateDuration = (duration) => {
    const secs = parseInt(duration % 60, 10)
    const mins = parseInt((duration % 3600) / 60, 10)
    const hours = parseInt(duration / 3600, 10)

    return {
      hours: calculatePadding(hours),
      minutes: calculatePadding(mins.toFixed()),
      seconds: calculatePadding(secs.toFixed()),
    }
  }

  const showVideoControlsHandler = () => {
    setIsControlsVisible(true)
  }

  const hideVideoControlsHandler = () => {
    if (!isVideoPaused) setIsControlsVisible(false)
  }

  const muteAndUnmuteHandler = () => {
    if (videoElementRef.current.muted) {
      videoElementRef.current.muted = false
      setIsVideoMuted(false)
      if (isUnmuteBtnVisible) setIsUnmuteBtnVisible(false)
    } else {
      videoElementRef.current.muted = true
      setIsVideoMuted(true)
    }
  }

  const playAndPauseHandler = () => {
    if (isUnmuteBtnVisible) {
      muteAndUnmuteHandler()
      return
    }
    const isPlaying =
      videoElementRef.current.currentTime > 0 &&
      !videoElementRef.current.paused &&
      !videoElementRef.current.ended &&
      videoElementRef.current.readyState >
        videoElementRef.current.HAVE_CURRENT_DATA
    if (!isPlaying) {
      const playPromise = videoElementRef.current.play()
      if (playPromise) {
        playPromise
          .then(() => {
            setIsVideoPaused(false)
          })
          .catch(() => {
            setIsVideoPaused(true)
          })
      }
      if (isMobileOrTablet() && isControlsVisible) {
        setTimeout(() => {
          setIsControlsVisible(false)
        }, 1000)
      }
    } else {
      videoElementRef.current.pause()
      setIsVideoPaused(true)
      setIsControlsVisible(true)
    }
  }

  const exitFromFullScreen = () => {
    setIsVideoFullScreen(false)
    if (document.exitFullscreen) document.exitFullscreen()
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen()
    else if (document.webkitCancelFullscreen) document.webkitCancelFullscreen()
    else if (document.msExitFullscreen) document.msExitFullscreen()
  }

  const launchIntoFullScreen = () => {
    setIsVideoFullScreen(true)
    if (videoContainerElementRef.current.requestFullscreen)
      videoContainerElementRef.current.requestFullscreen()
    else if (videoContainerElementRef.current.mozRequestFullScreen)
      videoContainerElementRef.current.mozRequestFullScreen()
    else if (videoContainerElementRef.current.webkitRequestFullscreen)
      videoContainerElementRef.current.webkitRequestFullscreen()
    else if (videoContainerElementRef.current.msRequestFullscreen)
      videoContainerElementRef.current.msRequestFullscreen()
  }

  const toggleFullScreenHandler = () => {
    if (checkIsFullScreen()) exitFromFullScreen()
    else launchIntoFullScreen()
  }

  const updateSeekbar = (currentTime) => {
    const totalDurationTime =
      +totalDurationHours * 3600 + +totalDurationMins * 60 + +totalDurationSecs
    if (totalDurationTime > 0 && currentTime !== 0) {
      const totalCurrentTime =
        +currentTime.hours * 3600 +
        +currentTime.minutes * 60 +
        +currentTime.seconds
      const seekPercentageValue = (totalCurrentTime / totalDurationTime) * 100
      setSeekbarPercentage(+seekPercentageValue.toFixed())
    } else setSeekbarPercentage(0)
  }

  const loadVideoDataHandler = () => {
    const totalDurationInSecs = videoElementRef.current.duration
    if (totalDurationInSecs) {
      const totalDuration = calculateDuration(totalDurationInSecs)
      setTotalDurationHours(totalDuration.hours)
      setTotalDurationMins(totalDuration.minutes)
      setTotalDurationSecs(totalDuration.seconds)
      updateSeekbar(0)
    }
  }

  const updateVideoTimingHandler = () => {
    const currentTimeInSec = videoElementRef.current.currentTime
    if (currentTimeInSec) {
      const currentDuration = calculateDuration(currentTimeInSec)
      setCurrentTimeHours(currentDuration.hours)
      setCurrentTimeMins(currentDuration.minutes)
      setCurrentTimeSecs(currentDuration.seconds)
      updateSeekbar(currentDuration)
    }
  }

  const videoSeekbarDurationHandler = (e) => {
    const newCurrentTime =
      e.currentTarget.value / (100 / videoElementRef.current.duration)
    videoElementRef.current.currentTime = newCurrentTime
  }

  const videoEndedHandler = () => {
    videoElementRef.current.currentTime = 0
    setIsVideoPaused(true)
    if (isMobileOrTablet()) setIsControlsVisible(false)
  }

  return (
    <VideoPlayerContainer>
      <VideoContainer
        ref={videoContainerElementRef}
        onMouseEnter={showVideoControlsHandler}
        onMouseLeave={hideVideoControlsHandler}
      >
        <Video
          ref={videoElementRef}
          onClick={playAndPauseHandler}
          onLoadedData={loadVideoDataHandler}
          onTimeUpdate={updateVideoTimingHandler}
          onEnded={videoEndedHandler}
          loop={videoLoop}
          autoPlay={videoAutoPlay}
          muted={videoMuted}
          poster={
            isHindiUgcUser && (isSeries
              ? 'https://djhonz7dexnot.cloudfront.net/e5ac1a7398f41b0ab91abcaee6e4da0ad54bcf7e.jpg'
              : 'https://djhonz7dexnot.cloudfront.net/b489d24cbd37614579bb7532c92e8afb5f99c6f6.jpg'
          )}
        >
          <source src={videoSrc} type={videoType} />
        </Video>
        <VideoControls isVisible={isControlsVisible}>
          <VideoPlaybackControls>
            <PlayAndPauseBtn onClick={playAndPauseHandler}>
              {isVideoPaused ? (
                <PlayIcon
                  fill="#ffffff"
                  fillOpacity="1"
                  width="15"
                  height="15"
                />
              ) : (
                <PauseIcon
                  fill="#ffffff"
                  fillOpacity="1"
                  width="15"
                  height="15"
                />
              )}
            </PlayAndPauseBtn>
            <MuteAndUnmuteBtn onClick={muteAndUnmuteHandler}>
              {isVideoMuted ? (
                <VolumeMuteIcon
                  fill="#ffffff"
                  fillOpacity="1"
                  width="15"
                  height="15"
                />
              ) : (
                <VolumeUpIcon
                  fill="#ffffff"
                  fillOpacity="1"
                  width="15"
                  height="15"
                />
              )}
            </MuteAndUnmuteBtn>
            {hideVideoPlayer && (
              <CloseVideoPlayerBtn onClick={hideVideoPlayer}>
                <CrossIcon fill="#ffffff" width="13" height="13" />
              </CloseVideoPlayerBtn>
            )}
            <VideoTimings>
              {!isMobileOrTablet() && (
                <FullScreenBtn onClick={toggleFullScreenHandler}>
                  {isVideoFullScreen ? (
                    <ExitFullScreenIcon
                      fill="#ffffff"
                      fillOpacity="1"
                      width="15"
                      height="15"
                    />
                  ) : (
                    <EnterFullScreenIcon
                      fill="#ffffff"
                      fillOpacity="1"
                      width="15"
                      height="15"
                    />
                  )}
                </FullScreenBtn>
              )}
              {currentTimeHours !== '00' ? (
                <Time>
                  {currentTimeHours}:{currentTimeMins}:{currentTimeSecs}
                </Time>
              ) : (
                <Time>
                  {currentTimeMins}:{currentTimeSecs}
                </Time>
              )}
              {totalDurationHours !== '00' ? (
                <Time>
                  / {totalDurationHours}:{totalDurationMins}:{totalDurationSecs}
                </Time>
              ) : (
                <Time>
                  / {totalDurationMins}:{totalDurationSecs}
                </Time>
              )}
            </VideoTimings>
          </VideoPlaybackControls>
          <VideoSeekBar
            type="range"
            min="0"
            max="100"
            step="1"
            bgValue={seekbarPercentage}
            value={seekbarPercentage}
            onInput={videoSeekbarDurationHandler}
            onChange={videoSeekbarDurationHandler}
          />
        </VideoControls>
        {isMobileOrTablet() && isUnmuteBtnVisible && (
          <UnmuteBtn onClick={muteAndUnmuteHandler}>
            <VolumeMuteIcon
              fill="#000000"
              fillOpacity="1"
              width="25"
              height="25"
            />
            <span>tap to unmute</span>
          </UnmuteBtn>
        )}
      </VideoContainer>
    </VideoPlayerContainer>
  )
}

export default VideoPlayer
